<template>
  <div id="appwrapper">
    <global-loaders />
    <template v-if="showPanel">
      <main-menu-mobile-drawer />
      <main-menu-grouping-mobile-dropdown class="hidden-md-and-up" />
      <main-menu-report-tabs-mobile-dropdown class="hidden-lg-and-up" />
    </template>

    <v-app
      :key="String(locale)"
      :class="vAppClass"
    >
      <app-exceptions-resolver v-if="showExceptionsResolver" />

      <template v-if="!store.panelBlocked && showPanel">
        <app-banners />
        <app-navbar />
        <app-content />
      </template>

      <router-view v-if="!showPanel" />
      <dialogs />
    </v-app>

    <intro-js v-if="store.tutorial.quickStart" />
    <devbox />
    <toast />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { throttle } from 'lodash';
import Devbox from '@/components/devbox.vue';
import MainMenuGroupingMobileDropdown from '@/views/layout/navigation/grouping/main-menu-grouping-mobile-dropdown.vue';
import MainMenuReportTabsMobileDropdown from '@/views/layout/navigation/grouping/main-menu-report-tabs-mobile-dropdown.vue';
import AppNavbar from './views/layout/navbar.vue';
import AppContent from './views/layout/content.vue';
import AppBanners from '@/components/app-banners/app-banners.vue';
import Toast from '@/components/toast.vue';
import introJs from '@/components/tutorial/quickstart.vue';
import MainMenuMobileDrawer from '@/views/layout/navigation/main-menu-mobile-drawer.vue';
import AppExceptionsResolver from '@/components/app-exceptions-resolver/app-exceptions-resolver.vue';
import Dialogs from '@/components/dialogs/index.vue';
import GlobalLoaders from '@/components/global-loaders/global-loaders.vue';
import { useGlobalErrorsHandler } from '@/services/global-errors-handlers';
import { useI18n, useTutorialApi, useUrl, isSettingsRouteName, useTableQuery } from '@/composables';
import { ColumnsLocalStorageKey } from '@/services/columns';
import { switchDesktopMobileView } from '@/routes/hooks/desktop-view-forced';
import { $emitter } from '@/services/emitter';
import { logoutChatwootSession, syncChatwootUser } from '@/services/utils/chatwoot';
import { getIntercomBootPayload } from '@/services/utils/support-widget';
import { useStore } from '@/store';
import type { User } from '@/services/models';

const forceHeaderHideList = [
  'login.member',
  'login',
  'login.support',
  'two-factor',
  'register',
  'register-currency',
  'register-email-verification',
  'dev.translations',
  'dev.storybook',
];

const getWidth = () =>
  Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth,
  );

const { locale } = useI18n();
const route = useRoute();
const { getLink } = useUrl();
const store = useStore();
const tableQueryApi = useTableQuery();
const { initErrorsHandler } = useGlobalErrorsHandler();
const tutorialApi = useTutorialApi(getLink);

const width = ref(getWidth());

const intercom = (window as Window & {
  $intercom?: {
    boot: (options: Record<string, unknown>) => void;
    hide: () => void;
    shutdown: () => void;
  };
}).$intercom;

const isMobile = computed(() => width.value < 960);

const quickStartCondition = computed(
  () =>
    store.tutorial.quickStart ||
    store.tutorial.quickStartStep ||
    store.tutorial.quickStartHiddenClass,
);

const quickStartHideCondition = computed(
  () => isMobile.value && quickStartCondition.value,
);

const vAppClass = computed(() => ({
  'quick-start-pad': !!store.tutorial.quickStart,
  'hidden-bg': !!store.tutorial.quickStartHiddenClass,
}));

const isForceHeaderHide = computed(() => {
  const routeName = route.name;
  if (!routeName) {
    return false;
  }
  return forceHeaderHideList.includes(String(routeName));
});

const showPanel = computed(
  () =>
    store.isAuthenticated &&
    store.isAuthorized &&
    store.isProfileLoaded &&
    !isForceHeaderHide.value,
);

const showExceptionsResolver = computed(
  () =>
    store.isAuthenticated &&
    route.name !== 'register-currency' &&
    route.name !== 'register-email-verification',
);

const checkMobileView = () => {
  if (store.desktopViewForced == null) {
    store.setDesktopViewForced(false);
  }
};

const onWindowResize = () => {
  width.value = getWidth();
  document.documentElement.style.setProperty('--windowInnerHeight', `${window.innerHeight}px`);
  $emitter.emit('window-resized');
};

const onWindowResizeThrottled = throttle(onWindowResize, 500);

const handleRouteChange = (
  value: typeof route,
  oldValue: typeof route | undefined,
) => {
  store.setReportRoute({
    name: value?.name ? String(value.name) : undefined,
    type: value?.params?.type as string | undefined,
  });

  document.body.dataset.view = value?.name ? String(value.name) : '';

  if (value.params?.report) {
    const reportId = value.params.report as string;
    store.setTableSelectedReport(reportId);
    if (!store.table.activeTab) {
      const matchingTab = store.table.activeReports.find((item) => item.id === reportId);
      if (matchingTab) {
        store.navigateTableActiveTab({ tabId: matchingTab.tabId });
      }
    }
  } else if (isSettingsRouteName(value?.name)) {
    store.navigateTableActiveTab({
      tabId: -1,
      url: oldValue?.fullPath,
    });
  } else {
    store.navigateTableActiveTab({
      tabId: 0,
      url: oldValue?.fullPath,
    });
  }
};

const reloadIntercom = (user: User | null, oldUser: User | null) => {
  if (user?.id === oldUser?.id) {
    return;
  }

  if (user?.id) {
    if (user.featureFlags?.chatwoot?.enabled) {
      void syncChatwootUser(user);
    } else if (store.isAuthenticated && store.isAuthorized) {
      intercom?.boot(getIntercomBootPayload(user));

      intercom?.hide();
    }

    return;
  }

  if (oldUser?.featureFlags?.chatwoot?.enabled) {
    logoutChatwootSession();
  } else {
    intercom?.shutdown();
  }
};

if (store.mobile.isMobile) {
  checkMobileView();
  switchDesktopMobileView(store.desktopViewForced as boolean);
} else {
  store.setDesktopViewForced(null);
}

watch(route, handleRouteChange, { immediate: true });

watch(
  quickStartHideCondition,
  (value) => {
    if (value) {
      tutorialApi.close();
      store.setTutorialQuickStart(false);
    }
  },
  { immediate: true },
);

watch(
  () => tableQueryApi.tableChartOpen.value,
  (value) => {
    $emitter.emit('datatable-resizer-redraw', value);
  },
);

watch(
  () => store.user,
  (user, oldUser) => {
    reloadIntercom(user, oldUser);
  },
);

onMounted(() => {
  initErrorsHandler();
  $emitter.emit('render:start');
  (window as { loader?: { hide: () => void } }).loader?.hide();

  document.body.dataset.view = route.name ? String(route.name) : '';

  if (!window.localStorage['auth-token']) {
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach((key) => {
      if (key !== ColumnsLocalStorageKey) {
        localStorage.removeItem(key);
      }
    });
  }

  window.addEventListener('resize', onWindowResizeThrottled);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResizeThrottled);
});
</script>

<style lang="scss">
.application, .application--wrap nav.v-toolbar {
  // padding-left: 380px !important;
}

.v-toolbar.quick-start-pad, .navbar.quick-start-pad, .v-content.quick-start-pad, .app-content.quick-start-pad {
  padding-left: 380px !important;
}

//disable animations
* {
  transition: none !important;
}

.tab-transition {
  &-enter {
    transform: none;
    display: none;
  }

  &-leave,
  &-leave-active {
    position: static;
  }


  &-leave-to {
    position: static;
    transform: none;
  }
}

.tab-reverse-transition {
  &-enter {
    transform: none;
    display: none;
  }


  &-leave, &-leave-to {
    position: static;
    transform: none;
  }
}


.picker-title-transition {
  &-enter-to,
  &-leave {
    transform: none;
  }


  &-enter {
    transform: none;
  }


  &-leave-to {
    opacity: 1;
    transform: none;
  }


  &-leave,
  &-leave-to,
  &-leave-active {
    position: static !important
  }
}

.picker-transition {
  &-enter {
    transform: none;
  }


  &-leave-to {
    transform: none;
  }
}

.picker-transition,
.picker-reverse-transition {
  &-enter-active,
  &-leave-active {
    transition: none;
  }


  &-enter,
  &-leave-to {
    opacity: 1
  }


  &-leave,
  &-leave-active,
  &-leave-to {
    position: static !important
  }
}

</style>
